import type {
  Assignment,
  BookingSegment,
  Conflict,
  EffectiveRule,
  RuleDefinition,
  RuleEvaluation,
  RuleOccurrence,
  RulePreview,
  RuleRange,
  Workspace,
} from "./model";
import { resolveEstimate } from './estimates';
import { deriveStayCycles, cycleForContext, earlyAuthorization } from './stay-cycles';
import {
  addDays,
  daysBetween,
  durationMinutes,
  isoAddHours,
  localDate,
  localTime,
  localToInstant,
  monthDate,
  stableId,
  operationalNow,
  todayAt,
  weekday,
} from "./time";

export function effectiveRules(
  state: Workspace,
  unitId: string,
): EffectiveRule[] {
  const unit = state.units.find((item) => item.id === unitId);
  if (!unit) return [];
  return state.rules
    .filter(
      (rule) =>
        rule.status !== "retired" &&
        (rule.scope === "unit"
          ? rule.scopeId === unitId
          : rule.scopeId === unit.propertyId),
    )
    .map((rule) => ({
      rule,
      inherited: rule.scope === "property",
      disabledHere:
        rule.scope === "property" &&
        unit.disabledInheritedRuleIds.includes(rule.id),
      reason: unit.disabledRuleReasons?.[rule.id],
    }));
}

/** Keep prior revisions effective before the activation date; new revisions never rewrite that past. */
export function workspaceWithRule(
  state: Workspace,
  rule: RuleDefinition,
): Workspace {
  const old = state.rules.find((item) => item.id === rule.id);
  const before = addDays(rule.effectiveFrom, -1);
  const history = (state.ruleHistory ?? [])
    .map((item) =>
      item.id === rule.id && (!item.effectiveTo || item.effectiveTo > before)
        ? { ...item, effectiveTo: before }
        : item,
    )
    .filter(
      (item) => !item.effectiveTo || item.effectiveTo >= item.effectiveFrom,
    );
  if (old && old.effectiveFrom <= before)
    history.push({
      ...old,
      effectiveTo:
        old.effectiveTo && old.effectiveTo < before ? old.effectiveTo : before,
    });
  return {
    ...state,
    ruleHistory: history,
    rules: [
      ...state.rules.filter((item) => item.id !== rule.id),
      { ...rule, revision: (old?.revision ?? 0) + 1 },
    ],
  };
}

/** A continuity link is only a grouping; corrections always retain original segments. */
export interface BookingStay extends BookingSegment {
  arrivalAnchorId: string;
  departureAnchorId: string;
  sourceBookingIds: string[];
}
export function bookingStays(state: Workspace, unitId: string): BookingStay[] {
  const bookings = state.bookings.filter(
    (item) => item.unitId === unitId && item.status === "confirmed",
  );
  const grouped = new Set<string>();
  const result: BookingStay[] = [];
  for (const stay of state.stays.filter((item) => item.valid)) {
    const segments = bookings
      .filter((item) => stay.bookingIds.includes(item.id))
      .sort((a, b) => a.arrival.localeCompare(b.arrival));
    if (segments.length !== stay.bookingIds.length || segments.length < 2)
      continue;
    segments.forEach((item) => grouped.add(item.id));
    result.push({
      ...segments[0],
      id: stay.id,
      arrival: segments[0].arrival,
      departure: segments[segments.length - 1].departure,
      continuityId: stay.id,
      arrivalAnchorId: segments[0].id,
      departureAnchorId: segments[segments.length - 1].id,
      sourceBookingIds: segments.map((item) => item.id),
    });
  }
  return [
    ...bookings
      .filter((item) => !grouped.has(item.id))
      .map((item) => ({
        ...item,
        arrivalAnchorId: item.id,
        departureAnchorId: item.id,
        sourceBookingIds: [item.id],
      })),
    ...result,
  ].sort(
    (a, b) => a.arrival.localeCompare(b.arrival) || a.id.localeCompare(b.id),
  );
}

export function validateRule(rule: RuleDefinition, state: Workspace): void {
  if (
    !rule.name.trim() ||
    !state.services.some((item) => item.id === rule.serviceId)
  )
    throw new Error("Give the rule a name and choose an existing service.");
  if (
    !state.properties.some(
      (item) => rule.scope === "property" && item.id === rule.scopeId,
    ) &&
    !state.units.some(
      (item) => rule.scope === "unit" && item.id === rule.scopeId,
    )
  )
    throw new Error("Choose an existing rule scope.");
  if (!Number.isSafeInteger(rule.trigger.interval) || rule.trigger.interval < 1)
    throw new Error("Frequency must be a positive whole number.");
  if (rule.window.dueOffsetHours < rule.window.earliestOffsetHours)
    throw new Error(
      "The ready-by deadline must not precede the earliest start.",
    );
  if ((rule.estimateMinutes ?? 1) <= 0 || (rule.crewSize ?? 1) < 1)
    throw new Error("Work estimates and crew size must be positive.");
  if (rule.trigger.kind === "calendar" && !rule.trigger.anchorDate)
    throw new Error("Choose an anchor date for the calendar schedule.");
  if (rule.window.endTime <= rule.window.startTime)
    throw new Error("Allowed working hours must end after they start.");
}

function recurrenceDate(date: string, rule: RuleDefinition): boolean {
  const anchor = rule.trigger.anchorDate!;
  if (date < anchor) return false;
  const interval = rule.trigger.interval;
  const days = daysBetween(anchor, date);
  if (rule.trigger.unit === "days") return days % interval === 0;
  if (rule.trigger.unit === "weeks")
    return (
      Math.floor(days / 7) % interval === 0 &&
      (rule.trigger.weekdays?.length
        ? rule.trigger.weekdays
        : [weekday(anchor)]
      ).includes(weekday(date))
    );
  const months =
    (Number(date.slice(0, 4)) - Number(anchor.slice(0, 4))) * 12 +
    Number(date.slice(5, 7)) -
    Number(anchor.slice(5, 7));
  return (
    months % interval === 0 &&
    monthDate(
      anchor,
      months,
      rule.trigger.monthDay ?? Number(anchor.slice(8)),
      rule.trigger.invalidDay,
    ) === date
  );
}
function conditionMatches(
  rule: RuleDefinition,
  date: string,
  tags: string[],
  type: string,
  booking?: BookingSegment,
  timeZone?: string,
): { matches: boolean; unknown: boolean } {
  const checks: boolean[] = [];
  if (rule.conditions.weekdays.length)
    checks.push(rule.conditions.weekdays.includes(weekday(date)));
  if (rule.conditions.months.length)
    checks.push(rule.conditions.months.includes(Number(date.slice(5, 7))));
  if (rule.conditions.tags.length)
    checks.push(rule.conditions.tags.every((tag) => tags.includes(tag)));
  if (rule.conditions.unitTypes.length)
    checks.push(rule.conditions.unitTypes.includes(type));
  if (rule.conditions.minNights !== undefined) {
    if (!booking) return { matches: true, unknown: true };
    checks.push(
      daysBetween(
        localDate(booking.arrival, timeZone),
        localDate(booking.departure, timeZone),
      ) >= rule.conditions.minNights,
    );
  }
  return {
    matches:
      !checks.length ||
      (rule.conditions.match === "any"
        ? checks.some(Boolean)
        : checks.every(Boolean)),
    unknown: false,
  };
}

type TriggerEvent = {
  key: string;
  context: string;
  instant: string;
  booking?: BookingSegment;
  earliest?: string;
  due?: string;
  missing?: string;
};
function triggerEvents(
  state: Workspace,
  rule: RuleDefinition,
  unitId: string,
  timeZone: string,
  range: RuleRange,
): TriggerEvent[] {
  const stays = bookingStays(state, unitId);
  const events: TriggerEvent[] = [];
  const at = (date: string) =>
    localToInstant(date, rule.window.startTime, timeZone);
  if (rule.trigger.kind === "checkout" || rule.trigger.kind === "checkin") {
    stays.forEach((stay, index) => {
      const outgoing =
        rule.trigger.kind === "checkout" ? stay : stays[index - 1];
      const next =
        rule.trigger.kind === "checkout"
          ? stays.find(
              (item) => item.arrival >= stay.departure && item.id !== stay.id,
            )
          : stay;
      const instant =
        rule.trigger.kind === "checkout" ? stay.departure : stay.arrival;
      const anchor =
        rule.trigger.kind === "checkout"
          ? stay.departureAnchorId
          : stay.arrivalAnchorId;
      events.push({
        key: `${rule.trigger.kind}:${anchor}`,
        context: outgoing
          ? `turnover:${unitId}:${outgoing.departureAnchorId}`
          : `arrival:${unitId}:${stay.arrivalAnchorId}`,
        instant,
        booking: stay,
        earliest: outgoing?.departure,
        due: next?.arrival,
      });
    });
  } else if (rule.trigger.kind === "stay-frequency") {
    for (const stay of stays) {
      const arrival = localDate(stay.arrival, timeZone);
      const departure = localDate(stay.departure, timeZone);
      for (
        let night = rule.trigger.interval;
        night < daysBetween(arrival, departure);
        night += rule.trigger.interval
      ) {
        const date = addDays(arrival, night);
        if (date >= range.from && date <= range.to)
          events.push({
            key: `stay:${stay.arrivalAnchorId}:${night}`,
            context: `stay:${unitId}:${stay.arrivalAnchorId}:${night}`,
            instant: at(date),
            booking: stay,
            earliest: stay.arrival,
            due: stay.departure,
          });
      }
    }
  } else if (rule.trigger.kind === "calendar") {
    if (!rule.trigger.anchorDate)
      return [
        {
          key: "missing-anchor",
          context: `calendar:${unitId}:${rule.id}`,
          instant: at(range.from),
          missing: "Choose an initial anchor date.",
        },
      ];
    for (let date = range.from; date <= range.to; date = addDays(date, 1))
      if (recurrenceDate(date, rule))
        events.push({
          key: `calendar:${date}`,
          context: `calendar:${unitId}:${date}`,
          instant: at(date),
        });
  } else {
    const completed = state.assignments
      .filter(
        (item) =>
          item.unitId === unitId &&
          item.status === "completed" &&
          item.serviceId ===
            (rule.trigger.qualifyingServiceId || rule.serviceId) &&
          item.completedAt,
      )
      .sort((a, b) => b.completedAt!.localeCompare(a.completedAt!));
    if (rule.trigger.kind === "service-completed") {
      for (const item of completed)
        events.push({
          key: `completed:${item.id}`,
          context: `completion:${unitId}:${item.id}`,
          instant: item.completedAt!,
        });
    } else {
      const completion = completed[0];
      const anchor = completion
        ? localDate(completion.completedAt!, timeZone)
        : rule.trigger.anchorDate;
      if (!anchor)
        return [
          {
            key: "missing-anchor",
            context: `frequency:${unitId}:${rule.id}`,
            instant: at(range.from),
            missing:
              "Set a first due anchor or record a qualifying completion.",
          },
        ];
      const date =
        rule.trigger.unit === "months"
          ? monthDate(
              anchor,
              rule.trigger.interval,
              Number(anchor.slice(8)),
              rule.trigger.invalidDay,
            )
          : addDays(
              anchor,
              rule.trigger.interval * (rule.trigger.unit === "weeks" ? 7 : 1),
            );
      if (date)
        events.push({
          key: completion ? `since:${completion.id}` : `anchor:${anchor}`,
          context: `frequency:${unitId}:${rule.serviceId}:${completion?.id ?? anchor}`,
          instant: at(date),
        });
    }
  }
  return events;
}

function shiftedInstant(
  event: string,
  rule: RuleDefinition,
  timeZone: string,
): string {
  return rule.trigger.offsetUnit === "hours"
    ? isoAddHours(event, rule.trigger.offset)
    : localToInstant(
        addDays(localDate(event, timeZone), rule.trigger.offset),
        localTime(event, timeZone),
        timeZone,
      );
}
function workingDate(
  rule: RuleDefinition,
  earliest: string,
  due: string,
  minutes: number,
  timeZone: string,
): string | null {
  if (due < earliest) return null;
  for (
    let date = localDate(earliest, timeZone);
    date <= localDate(due, timeZone);
    date = addDays(date, 1)
  ) {
    if (
      rule.window.allowedWeekdays.length &&
      !rule.window.allowedWeekdays.includes(weekday(date))
    )
      continue;
    const start = localToInstant(date, rule.window.startTime, timeZone);
    const end = localToInstant(date, rule.window.endTime, timeZone);
    const begin = earliest > start ? earliest : start;
    const finish = due < end ? due : end;
    if (durationMinutes(begin, finish) >= minutes) return date;
  }
  return null;
}

/** Deterministic desired requirements. No storage, clocks, mutations, or shift-owned snapshots. */
export function evaluateRules(
  state: Workspace,
  range: RuleRange,
): RuleEvaluation {
  const occurrences: RuleOccurrence[] = [];
  const assignments: Assignment[] = [];
  const conflicts: Conflict[] = [];
  const separate = new Set<string>();
  const previousOccurrences = new Map(
    state.occurrences.map((item) => [item.id, item]),
  );
  const previousAssignments = new Map<string, Assignment>();
  const previousByIdentity = new Map(state.assignments.map(item => [item.id,item]));
  for (const occurrence of state.occurrences) {
    const assignment = occurrence.assignmentId ? previousByIdentity.get(occurrence.assignmentId) : undefined;
    if (assignment) previousAssignments.set(occurrence.id,assignment);
  }
  for (const assignment of state.assignments)
    for (const occurrenceId of assignment.occurrenceIds)
      if (!previousAssignments.has(occurrenceId))
        previousAssignments.set(occurrenceId, assignment);
  const generatedById = new Map<string, Assignment>();
  const groups = new Map<string, Assignment[]>();
  const cycles=deriveStayCycles(state);
  for (const unit of state.units.filter((item) => !item.archived)) {
    const timeZone =
      state.properties.find((item) => item.id === unit.propertyId)?.timeZone ??
      "Europe/Helsinki";
    const historic = (state.ruleHistory ?? [])
      .filter(
        (rule) =>
          rule.enabled &&
          rule.status === "active" &&
          (rule.scope === "unit"
            ? rule.scopeId === unit.id
            : rule.scopeId === unit.propertyId &&
              !unit.disabledInheritedRuleIds.includes(rule.id)),
      )
      .map((rule) => ({
        rule,
        inherited: rule.scope === "property",
        disabledHere: false,
      }));
    for (const effective of [
      ...effectiveRules(state, unit.id).filter(
        (item) =>
          !item.disabledHere &&
          item.rule.enabled &&
          item.rule.status === "active",
      ),
      ...historic,
    ].sort((a, b) => a.rule.id.localeCompare(b.rule.id))) {
      const rule = effective.rule;
      const service = state.services.find((item) => item.id === rule.serviceId);
      if (!service) continue;
      let events: TriggerEvent[];
      try {
        events = triggerEvents(state, rule, unit.id, timeZone, range);
      } catch (error) {
        events = [
          {
            key: "clock-conflict",
            context: `clock:${unit.id}:${rule.id}`,
            instant: localToInstant(range.from, "12:00", timeZone),
            missing:
              error instanceof Error
                ? error.message
                : "Review the local clock time.",
          },
        ];
      }
      for (const event of events) {
        let trigger: string;
        try {
          trigger = shiftedInstant(event.instant, rule, timeZone);
        } catch {
          trigger = event.instant;
          event.missing =
            "The local trigger time is ambiguous or does not exist.";
        }
        if (
          rule.trigger.kind === "completion" &&
          localDate(trigger, timeZone) < rule.effectiveFrom
        )
          trigger = localToInstant(
            rule.effectiveFrom,
            rule.window.startTime,
            timeZone,
          );
        const nominalDate = localDate(trigger, timeZone);
        const overdueCompletion =
          rule.trigger.kind === "completion" && nominalDate < range.from;
        if (
          (!overdueCompletion && nominalDate < range.from) ||
          nominalDate > range.to ||
          nominalDate < rule.effectiveFrom ||
          (rule.effectiveTo && nominalDate > rule.effectiveTo)
        )
          continue;
        const condition = conditionMatches(
          rule,
          localDate(event.instant, timeZone),
          unit.tags,
          unit.type,
          event.booking,
          timeZone,
        );
        if (!condition.matches) continue;
        const occurrenceId = stableId(
          "occ",
          rule.id,
          unit.id,
          rule.serviceId,
          event.key,
        );
        const previousOccurrence = previousOccurrences.get(occurrenceId);
        let previousAssignment = previousAssignments.get(occurrenceId);
        let earliestStart = isoAddHours(
          trigger,
          rule.window.earliestOffsetHours,
        );
        let dueAt = isoAddHours(trigger, rule.window.dueOffsetHours);
        if (event.earliest && event.earliest > earliestStart)
          earliestStart = event.earliest;
        if (event.due && event.due < dueAt) dueAt = event.due;
        const cycle=cycleForContext(state,unit.id,event.context,cycles);
        const authorization=earlyAuthorization(cycle);
        if (authorization && event.context.startsWith('turnover:') && service.readinessEffect==='cleaned') {
          earliestStart=authorization.from;
          if(authorization.to && authorization.to<dueAt)dueAt=authorization.to;
        }
        if(!previousAssignment&&cycle&&!rule.separateVisit){
          const fulfilled=(state.fulfillments??[]).find(item=>item.stayCycleId===cycle.id&&item.cycleRevision===cycle.serviceRevision&&item.source==='manual-link'&&item.ruleIds?.includes(rule.id)&&item.completedAt>=earliestStart&&item.completedAt<=dueAt);
          const completed=fulfilled?previousByIdentity.get(fulfilled.assignmentId):undefined;
          if(completed?.serviceId===service.id&&completed.status==='completed')previousAssignment=completed;
        }
        if (
          !previousAssignment &&
          rule.trigger.kind === "stay-frequency" &&
          ((event.booking as BookingStay | undefined)?.sourceBookingIds
            .length ?? 0) > 1
        ) {
          const sourceIds = (event.booking as BookingStay).sourceBookingIds;
          previousAssignment = state.assignments.find(
            (item) =>
              item.unitId === unit.id &&
              item.serviceId === service.id &&
              item.status === "completed" &&
              item.completedAt &&
              item.completedAt >= earliestStart &&
              item.completedAt <= dueAt &&
              sourceIds.some((sourceId) =>
                item.contextKey.startsWith(`stay:${unit.id}:${sourceId}:`),
              ),
          );
        }
        const estimate=resolveEstimate(state,{unitId:unit.id,serviceId:service.id,rule});
        const estimateMinutes = estimate.minutes;
        let date: string | null = null;
        let missing =
          event.missing ||
          (condition.unknown ? "Required booking information is missing." : "");
        try {
          date = workingDate(
            rule,
            earliestStart,
            dueAt,
            estimateMinutes,
            timeZone,
          );
        } catch (error) {
          missing =
            error instanceof Error
              ? error.message
              : "Review the local working hours.";
        }
        const suppressed =
          previousOccurrence?.status === "suppressed" ||
          previousAssignment?.status === "declined";
        const satisfied =
          previousAssignment?.status === "completed" ||
          previousOccurrence?.status === "satisfied";
        const occurrence: RuleOccurrence = {
          id: occurrenceId,
          ruleId: rule.id,
          ruleRevision: rule.revision,
          unitId: unit.id,
          serviceId: service.id,
          contextKey: event.context,
          nominalDate,
          earliestStart,
          dueAt,
          requiredSkills: rule.requiredSkills ?? service.requiredSkills,
          crewSize: rule.crewSize ?? service.crewSize,
          stayCycleId:cycle?.id,
          status: suppressed
            ? "suppressed"
            : satisfied
              ? "satisfied"
              : missing
                ? "needs-information"
                : date
                  ? "pending"
                  : "needs-scheduling",
          reasons: [
            rule.name,
            ...(missing ? [missing] : []),
            ...(!date && !missing
              ? [
                  "No eligible working time exists before the ready-by deadline.",
                ]
              : []),
          ],
        };
        const groupKey = JSON.stringify([unit.id, service.id, event.context]);
        const group = groups.get(groupKey) ?? [];
        let assignment =
          previousAssignment && (suppressed || satisfied)
            ? generatedById.get(previousAssignment.id)
            : !rule.separateVisit
              ? group.find(
                  (item) =>
                    !separate.has(item.id) &&
                    item.earliestStart! <= dueAt &&
                    item.dueAt! >= earliestStart &&
                    !["declined", "completed"].includes(item.status),
                )
              : undefined;
        if (assignment) {
          assignment.occurrenceIds.push(occurrenceId);
          assignment.earliestStart =
            assignment.earliestStart! > earliestStart
              ? assignment.earliestStart
              : earliestStart;
          assignment.dueAt =
            assignment.dueAt! < dueAt ? assignment.dueAt : dueAt;
          assignment.requiredSkills = [
            ...new Set([
              ...assignment.requiredSkills,
              ...(rule.requiredSkills ?? service.requiredSkills),
            ]),
          ];
          assignment.estimateMinutes = Math.max(
            assignment.estimateMinutes,
            estimateMinutes,
          );
          assignment.crewSize = Math.max(
            assignment.crewSize,
            rule.crewSize ?? service.crewSize,
          );
          if (rule.priority === "high") assignment.priority = "high";
          assignment.date = workingDate(
            rule,
            assignment.earliestStart!,
            assignment.dueAt!,
            assignment.estimateMinutes,
            timeZone,
          );
          if (!assignment.date && !suppressed && !satisfied)
            occurrence.status = "needs-scheduling";
        } else {
          const incompatible =
            !rule.separateVisit &&
            group.find(
              (item) =>
                !separate.has(item.id) &&
                (item.earliestStart! > dueAt || item.dueAt! < earliestStart),
            );
          const id =
            previousAssignment && !generatedById.has(previousAssignment.id)
              ? previousAssignment.id
              : stableId(
                  "assignment",
                  unit.id,
                  service.id,
                  event.context,
                  occurrenceId,
                );
          assignment = {
            id,
            unitId: unit.id,
            serviceId: service.id,
            date,
            earliestStart,
            dueAt,
            estimateMinutes,
            estimateSource:estimate.source,
            estimateProvenance:estimate,
            stayCycleId:cycle?.id,
            stayCycleRevision:cycle?.serviceRevision,
            requiredSkills: rule.requiredSkills ?? service.requiredSkills,
            preferredSkills: [],
            crewSize: rule.crewSize ?? service.crewSize,
            priority: rule.priority ?? "normal",
            status: suppressed ? "declined" : satisfied ? "completed" : "open",
            occurrenceIds: [occurrenceId],
            origin: service.kind === "inspection" ? "inspection" : "rule",
            notes: rule.name,
            revision: previousAssignment?.revision ?? 0,
            contextKey: event.context,
            ...(rule.trigger.kind === "service-completed"
              ? { dependsOn: [event.key.replace("completed:", "")] }
              : {}),
          };
          assignments.push(assignment);
          generatedById.set(id, assignment);
          group.push(assignment);
          groups.set(groupKey, group);
          if (rule.separateVisit) separate.add(id);
          if (incompatible)
            conflicts.push({
              id: stableId("rule-window-conflict", incompatible.id, id),
              assignmentId: id,
              unitId: unit.id,
              code: "rule-window-conflict",
              severity: "warning",
              message:
                "Rules request the same service for this cycle in incompatible windows. Separate requirements are retained for review.",
            });
        }
        occurrence.assignmentId = assignment.id;
        occurrences.push(occurrence);
        if (
          occurrence.status === "needs-scheduling" ||
          occurrence.status === "needs-information"
        )
          conflicts.push({
            id: `rule-${occurrence.id}`,
            unitId: unit.id,
            assignmentId: assignment.id,
            severity: "warning",
            code: occurrence.status,
            message: occurrence.reasons.slice(1).join(" "),
          });
      }
    }
  }
  const previousById = new Map(
    state.assignments.map((item) => [item.id, item]),
  );
  return {
    occurrences,
    assignments: assignments.map((assignment) => {
      const previous = previousById.get(assignment.id);
      const overrides = previous?.manualOverrides;
      const historical=previous?.estimateProvenance?.confidence==='unrecorded'?{estimateMinutes:previous.estimateMinutes,estimateSource:'fixed' as const,estimateProvenance:previous.estimateProvenance}:undefined;
      if (!overrides && !previous?.manualDate) return {...assignment,...historical};
      const estimate=overrides?.estimateSource?resolveEstimate(state,{unitId:assignment.unitId,serviceId:assignment.serviceId,assignment:{...assignment,manualOverrides:overrides},source:overrides.estimateSource}):undefined;
      return {
        ...assignment,
        ...(overrides ?? {}),
        ...(estimate?{estimateMinutes:estimate.minutes,estimateSource:estimate.source,estimateProvenance:estimate}:{}),
        earliestStart:
          overrides && "earliestStart" in overrides
            ? (overrides.earliestStart ?? undefined)
            : assignment.earliestStart,
        dueAt:
          overrides && "dueAt" in overrides
            ? (overrides.dueAt ?? undefined)
            : assignment.dueAt,
        ...(previous?.manualDate
          ? { date: previous.date, manualDate: true }
          : {}),
        ...(overrides ? { manualOverrides: overrides } : {}),
        ...historical,
      };
    }),
    conflicts,
  };
}

const changedShape = (item: Assignment) =>
  JSON.stringify([
    item.date,
    item.earliestStart,
    item.dueAt,
    item.estimateMinutes,
    item.requiredSkills,
    item.crewSize,
    item.occurrenceIds,
    item.status,
  ]);
export function reconcileRules(
  state: Workspace,
  range?: RuleRange,
  at = operationalNow(state),
): Workspace {
  const today = todayAt(state.properties[0]?.timeZone, at);
  range ??= { from: today, to: addDays(today, 42) };
  const desired = evaluateRules(state, range);
  const assignments = [...state.assignments];
  const impacts = [...state.impacts];
  const currentAt = at;
  const assignmentIndexes = new Map(
    assignments.map((item, index) => [item.id, index]),
  );
  const actualAssignmentIds = new Set(
    state.sessions.map((item) => item.assignmentId),
  );
  const committedByAssignment = new Map<string, typeof state.allocations>();
  for (const allocation of state.allocations)
    if (allocation.status === "committed")
      committedByAssignment.set(allocation.assignmentId, [
        ...(committedByAssignment.get(allocation.assignmentId) ?? []),
        allocation,
      ]);
  const committed = (id: string) => committedByAssignment.get(id) ?? [];
  for (const generated of desired.assignments) {
    const index = assignmentIndexes.get(generated.id);
    if (index === undefined) {
      assignmentIndexes.set(generated.id, assignments.length);
      assignments.push(generated);
      continue;
    }
    const old = assignments[index];
    const actual =
      old.status === "completed" ||
      old.status === "in-progress" ||
      old.status === "paused" ||
      actualAssignmentIds.has(old.id);
    const allocations = committed(old.id);
    if (
      !["completed", "declined"].includes(old.status) &&
      changedShape(old) !== changedShape(generated) &&
      allocations.length
    ) {
      for (const allocation of allocations) {
        const shift = state.shifts.find(
          (item) => item.id === allocation.shiftId,
        );
        if (!shift) continue;
        const id = stableId(
          "impact-rule",
          allocation.shiftId,
          generated.id,
          changedShape(generated),
        );
        if (
          !impacts.some((item) => item.id === id && item.status === "pending")
        ) {
          for (let i = 0; i < impacts.length; i++)
            if (
              impacts[i].id !== id &&
              impacts[i].status === "pending" &&
              impacts[i].shiftId === shift.id &&
              impacts[i].assignmentId === old.id &&
              impacts[i].action === "update-window"
            )
              impacts[i] = { ...impacts[i], status: "superseded" };
          const previous = impacts.findIndex((item) => item.id === id);
          const proposal = {
            id,
            shiftId: shift.id,
            propertyId: shift.propertyId,
            date: shift.date,
            subjectType: "assignment",
            subjectId: old.id,
            assignmentId: old.id,
            title: "Work requirements changed",
            before: JSON.stringify({
              date: old.date,
              earliestStart: old.earliestStart,
              dueAt: old.dueAt,
              estimateMinutes: old.estimateMinutes,
            }),
            after: JSON.stringify({
              date: generated.date,
              earliestStart: generated.earliestStart,
              dueAt: generated.dueAt,
              estimateMinutes: generated.estimateMinutes,
            }),
            action: "update-window" as const,
            status: "pending" as const,
            severity: "warning" as const,
            createdAt: currentAt,
            sourceRevision: state.revision + 1,
            proposedAssignment: generated,
          };
          if (previous < 0) impacts.push(proposal);
          else impacts[previous] = proposal;
        }
      }
    }
    assignments[index] =
      actual || old.status === "declined"
        ? {
            ...old,
            occurrenceIds: [
              ...new Set([...old.occurrenceIds, ...generated.occurrenceIds]),
            ],
          }
        : {
            ...generated,
            revision:
              old.revision +
              (changedShape(old) === changedShape(generated) ? 0 : 1),
            date:
              old.manualDate || allocations.length ? old.date : generated.date,
            ...(old.manualDate ? { manualDate: true } : {}),
          };
  }
  const desiredIds = new Set(desired.assignments.map((item) => item.id));
  for (let index = 0; index < assignments.length; index++) {
    const old = assignments[index];
    if (
      !["rule", "inspection"].includes(old.origin) ||
      desiredIds.has(old.id) ||
      old.status !== "open" ||
      old.manualDate ||
      actualAssignmentIds.has(old.id)
    )
      continue;
    const relevant = old.occurrenceIds.some((id) =>
      state.occurrences.some(
        (item) =>
          item.id === id &&
          item.nominalDate >= range.from &&
          item.nominalDate <= range.to,
      ),
    );
    if (!relevant) continue;
    const allocations = committed(old.id);
    if (!allocations.length)
      assignments[index] = {
        ...old,
        status: "cancelled",
        revision: old.revision + 1,
      };
    for (const allocation of allocations) {
      const shift = state.shifts.find((item) => item.id === allocation.shiftId);
      if (!shift) continue;
      const id = stableId(
        "impact-obsolete",
        shift.id,
        old.id,
        state.bookings
          .map((item) => `${item.id}:${item.revision}:${item.continuityId}`)
          .join(","),
        state.rules.map((item) => `${item.id}:${item.revision}`).join(","),
      );
      if (!impacts.some((item) => item.id === id))
        impacts.push({
          id,
          shiftId: shift.id,
          propertyId: shift.propertyId,
          date: shift.date,
          subjectType: "assignment",
          subjectId: old.id,
          assignmentId: old.id,
          title: "This work is no longer required by the current rules",
          before: "Included in this shift",
          after: "Return obsolete unstarted work to history",
          action: "release-work",
          status: "pending",
          severity: "warning",
          createdAt: currentAt,
          sourceRevision: state.revision + 1,
        });
    }
  }
  const occurrenceIds = new Set(desired.occurrences.map((item) => item.id));
  const occurrences = [
    ...state.occurrences
      .filter((item) => !occurrenceIds.has(item.id))
      .map((item) =>
        item.status === "pending" &&
        item.nominalDate >= range.from &&
        item.nominalDate <= range.to
          ? { ...item, status: "superseded" as const }
          : item,
      ),
    ...desired.occurrences,
  ];
  return { ...state, assignments, occurrences, impacts,stayCycles:deriveStayCycles(state) };
}

export function previewRule(
  state: Workspace,
  rule: RuleDefinition,
  range: RuleRange,
): RulePreview {
  const candidate = workspaceWithRule(state, {
    ...rule,
    status: rule.status === "retired" ? "retired" : "active",
  });
  const before = evaluateRules(state, range);
  const after = evaluateRules(candidate, range);
  const added = after.assignments.filter(
    (item) => !before.assignments.some((old) => old.id === item.id),
  );
  const changed = after.assignments.filter((item) =>
    before.assignments.some(
      (old) => old.id === item.id && changedShape(old) !== changedShape(item),
    ),
  );
  const removed = before.assignments.filter(
    (item) => !after.assignments.some((next) => next.id === item.id),
  );
  const unchanged = after.assignments.filter((item) =>
    before.assignments.some(
      (old) => old.id === item.id && changedShape(old) === changedShape(item),
    ),
  );
  const ids = new Set([...changed, ...removed].map((item) => item.id));
  return {
    ...after,
    baseRevision: state.revision,
    added,
    changed,
    removed,
    unchanged,
    affectedShiftIds: [
      ...new Set(
        state.allocations
          .filter(
            (item) => item.status === "committed" && ids.has(item.assignmentId),
          )
          .map((item) => item.shiftId),
      ),
    ],
  };
}

export function defaultRule(
  scopeId: string,
  date = todayAt(),
  scope: "property" | "unit" = "property",
): RuleDefinition {
  return {
    id: `rule-${globalThis.crypto.randomUUID()}`,
    revision: 1,
    name: "Turnover cleaning",
    scope,
    scopeId,
    serviceId: "cleaning",
    enabled: true,
    status: "draft",
    effectiveFrom: date,
    trigger: {
      kind: "checkout",
      interval: 1,
      unit: "days",
      invalidDay: "last-day",
      offset: 0,
      offsetUnit: "days",
    },
    conditions: {
      match: "all",
      weekdays: [],
      months: [],
      tags: [],
      unitTypes: [],
    },
    window: {
      earliestOffsetHours: 0,
      dueOffsetHours: 24,
      allowedWeekdays: [],
      startTime: "08:00",
      endTime: "18:00",
    },
    separateVisit: false,
  };
}
